import { useState } from "react";
import { receiptApi } from "../../services/businessApi";
import { Button } from "../common/Button";
import { Field } from "../common/Field";
import { useToast } from "../../context/ToastContext";

/**
 * A receipt, rendered so it can be HANDED OVER (#193).
 *
 * Decided 2026-08-15: this is something the beekeeper CAN produce if a
 * customer asks, at the moment money changes hands. So it is laid out to be
 * read by the person on the other side of the table -- turned around on a
 * phone, or printed -- not as a row in an admin screen.
 *
 * Everything shown comes from `snapshot`, which the server froze when the
 * receipt was issued. Deliberately NOT re-derived from the vendor or the
 * assignment: correcting a price next week must not change what somebody is
 * holding. If this component ever starts taking a clientId and looking
 * things up, that guarantee is gone.
 *
 * Its own component rather than part of VendorDetail because it is a
 * document, and because testing it inside that page would mean mocking the
 * whole vendor surface to assert on one total. (The same reasoning once
 * applied to PendingInviteBanner, which was retired in #312 when invites
 * moved into the attention list.)
 */
export function ReceiptView({ receipt, onClose }) {
  const { notify } = useToast();
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [method, setMethod] = useState("Email");
  // Local so the panel reflects a send without refetching the list behind it.
  const [recorded, setRecorded] = useState({
    sent_to: receipt.sent_to,
    sent_method: receipt.sent_method,
  });

  const snapshot = receipt.snapshot || {};
  const lines = snapshot.lines || [];

  async function recordSend() {
    if (!sentTo.trim()) {
      notify("Enter where you sent it", "warning");
      return;
    }
    setSending(true);
    try {
      const saved = await receiptApi.markSent(receipt.receipt_id, {
        sent_to: sentTo.trim(),
        sent_method: method,
      });
      setRecorded({ sent_to: saved.sent_to, sent_method: saved.sent_method });
      notify("Noted where it went", "success");
    } catch (err) {
      // The receipt itself is already issued and valid. Failing to record
      // the delivery must not read as "the receipt failed" -- that would
      // push someone into issuing a second one.
      notify(err.apiMessage || "Couldn't record that — the receipt itself is fine", "danger");
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      {/* The document. Kept visually plain: it is going to be looked at by
          a customer, and print stylesheets are simpler when the receipt is
          not wrapped in app chrome. */}
      <div className="receipt-document" style={{ border: "1px solid var(--color-border)", padding: "var(--space-4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "var(--space-2)" }}>
          <strong>{snapshot.kind === "Payment" ? "Payment received" : "Delivery"}</strong>
          {/* Both names, when there are two (#270). The customer is
              holding a slip that says T-4F2A19; the books say
              R-2026-0007. Printing only the official number would leave
              them unable to tell it is the same document. */}
          <span style={{ textAlign: "right" }}>
            {receipt.receipt_number}
            {receipt.client_reference && (
              <span style={{ display: "block", color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                issued offline as {receipt.client_reference}
              </span>
            )}
          </span>
        </div>
        <p style={{ margin: "var(--space-2) 0", color: "var(--color-text-muted)" }}>
          {snapshot.vendor} · {snapshot.date}
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td style={{ padding: "var(--space-1) 0" }}>{line.description}</td>
                <td style={{ padding: "var(--space-1) 0", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  ${Number(line.amount).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: "1px solid var(--color-border)",
            marginTop: "var(--space-2)",
            paddingTop: "var(--space-2)",
            fontWeight: 700,
          }}
        >
          <span>Total</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>${Number(receipt.total).toFixed(2)}</span>
        </div>

        {snapshot.notes && (
          <p style={{ marginTop: "var(--space-2)", color: "var(--color-text-muted)" }}>{snapshot.notes}</p>
        )}
      </div>

      {/* Handing it over is the point, so print sits with the document
          rather than in a menu. */}
      <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)", flexWrap: "wrap" }}>
        <Button onClick={() => window.print()}>Print</Button>
        {onClose && <Button variant="secondary" onClick={onClose}>Done</Button>}
      </div>

      {/* Recording where it went is OPTIONAL and comes after. Showing it on
          screen or handing over a printout leaves nothing to record --
          "issued but not sent" is a complete outcome (#193), so this section
          never nags. */}
      {recorded.sent_to ? (
        <p style={{ marginTop: "var(--space-3)", color: "var(--color-text-muted)" }}>
          Sent to {recorded.sent_to} by {String(recorded.sent_method).toLowerCase()}.
        </p>
      ) : (
        <details style={{ marginTop: "var(--space-3)" }}>
          <summary>Sent it somewhere? Make a note (optional)</summary>
          <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
            {/* Field is a render prop (#205): it hands back the generated
                id so the control keeps its own props, rather than cloning
                children and guessing which element to label. */}
            <Field label="Where it went">
              {(id) => (
                <input
                  id={id}
                  value={sentTo}
                  onChange={(e) => setSentTo(e.target.value)}
                  placeholder="e.g. shop@example.com"
                  className="touch-target"
                />
              )}
            </Field>
            <Field label="How">
              {(id) => (
                <select id={id} value={method} onChange={(e) => setMethod(e.target.value)} className="touch-target">
                  <option value="Email">Email</option>
                  <option value="SMS">Text message</option>
                  <option value="Printed">Printed and handed over</option>
                </select>
              )}
            </Field>
            <div>
              <Button onClick={recordSend} disabled={sending}>
                {sending ? "Saving…" : "Save note"}
              </Button>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
